import { createContext, useContext, ReactNode } from "react";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { GridProduct } from "src/api/client-api/products/useGetAllproduct";
import { useProductColumns } from "./useProductColumns";
import { MRT_ColumnDef } from "material-react-table";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { DelayedSearch, useDelayedSearch } from "src/utils/useDelayedSearch";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useAutoSyncGetAllProducts } from "src/api/client-api/products/getAll/useAutoSyncGetAllProducts";
import { useProductMenuItems } from "./ProductGrid/useProductMenuItems";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { AppSearch } from "src/api/_common/hydra/search";
import { ApiPagination } from "src/api/_common/params/useApiPaginationUrlSync";
import { isBrandCollectionError, useGetAllBrands, BrandCollectionState } from "src/api/client-api/brand/useGetAllBrand";
import type { TableAction } from "src/components/Grid/headers/GridTableActions";
import { useListExportAction } from "src/components/Grid/headers/useListExportAction";
import { useScoreRecalculationAction } from "src/components/Grid/headers/useScoreRecalculationAction";
import { productGridKey } from "src/api/client-api/products/useGetAllproduct";

export type GridColumns = MRT_ColumnDef<GridProduct, any>[];
export type ProductListContextInitialValues = {
    mrt_columns: GridColumns;
    products: GridProduct[];
    totalItems: number;
    appSearch: AppSearch | undefined;
    brandCollectionState: BrandCollectionState;

    menuItems: MenuItemType<GridProduct>[];
    tableActions: TableAction[];
    gridConfiguration: GridColumnsType<GridProduct>;

    globalSearch: DelayedSearch;
    isRefetching: boolean;
    isReady: boolean;
    gridKey: string;
    pagination: ApiPagination;
    refetchBrands: () => Promise<unknown>;
};
export const ProductListContext = createContext<ProductListContextInitialValues | null>(null);

type ProductListContextProviderProps = { children: ReactNode };
export const ProductListContextProvider = (props: ProductListContextProviderProps) => {
    const { children } = props;
    const gridKey = "product-grid" as const;

    const { canUser, selectedCompany, currentCampaign } = useFoodpilotContext();
    const { company } = useCompanyMetadata();

    const { allAsAxiosParams, filters, sort, pagination } = useUrlToApi({
        page: "1",
        itemsPerPage: "20",
        tagContext: "product",
    });
    const globalSearch = useDelayedSearch();
    const { products, isRefetching, appSearch, totalItems, isReady } = useAutoSyncGetAllProducts({
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
        params: {
            ...allAsAxiosParams,
            q: globalSearch.delayedSearch === "" ? undefined : globalSearch.delayedSearch,
            isIntermediate: false,
        },
        scoresQuery: {
            scores: {
                subscores: false,
            },
        },
    });

    const brandFilterQuery = useGetAllBrands({
        itemsPerPage: 1000,
        initSearch: "",
    });
    const brandExistenceQuery = useGetAllBrands({ itemsPerPage: 1 });
    const brandCollectionState = getBrandAuxiliaryQueryState(
        brandFilterQuery.brandCollectionState,
        brandExistenceQuery.brandCollectionState,
    );
    const refetchBrands = async () => {
        await Promise.all([brandFilterQuery.refetch(), brandExistenceQuery.refetch()]);
    };

    const mrt_columns = useProductColumns({
        brandExistenceQuery,
        brandFilterQuery,
        data: products,
        gridKey,
    });
    const gridConfiguration = useGridColumns(mrt_columns, gridKey);

    const menuItems = useProductMenuItems({
        canUser,
        companyIds: {
            "@id": company["@id"],
            id: company.id,
        },
    });

    const exportAction = useListExportAction({
        path: "/api/products",
        filters,
        sort,
        search: globalSearch.delayedSearch,
        campaignId: currentCampaign?.id,
        organizationId: selectedCompany?.id,
        extra: { isIntermediate: false },
        fileNameStem: "products",
        isDisabled: currentCampaign === null,
    });

    const scoreRecalculationAction = useScoreRecalculationAction({
        path: "/api/products",
        entityKind: "product",
        invalidatedQueryKey: productGridKey,
        pageEntities: (products ?? []).map(({ id, title }) => ({ id, name: title })),
        campaignId: currentCampaign?.id,
        organizationId: selectedCompany?.id,
    });

    const items: ProductListContextInitialValues = {
        appSearch,
        brandCollectionState,
        gridConfiguration,
        gridKey: gridKey,
        isRefetching,
        isReady,
        menuItems,
        tableActions: [exportAction, scoreRecalculationAction],
        mrt_columns,
        products: products ?? [],
        totalItems,
        globalSearch,
        pagination,
        refetchBrands,
    };

    return <ProductListContext.Provider value={items}>{children}</ProductListContext.Provider>;
};

const getBrandAuxiliaryQueryState = (
    filterState: BrandCollectionState,
    existenceState: BrandCollectionState,
): BrandCollectionState => {
    const errorState = [filterState, existenceState].find(isBrandCollectionError);

    return errorState ?? filterState;
};

export const useProductListContext = () => {
    const context = useContext(ProductListContext);

    if (context) {
        return context;
    }

    throw new Error(`useProductListContext must be used within an ProductListContextProvider`);
};
